<template>
  <div id="content">
    <v-card tile>
      <v-responsive max-height="500">
        <v-img class="grey white--text" :src="banner" max-height="500">
          <v-layout fill-height justify-left align-center>
            <h1 class="banner-title">Sermons</h1>
          </v-layout>
        </v-img>
      </v-responsive>
    </v-card>

    <!-- Featured sermons -->
    <v-container v-if="featuredSermons.length !== 0" fluid grid-list-xl>
      <h5 class="headline text-xs-center mt-5">Featured Sermons</h5>
      <horizontal-list :items="featuredSermons">
        <template slot-scope="sermon">
          <v-hover>
            <v-card
              slot-scope="{ hover }"
              class="d-flex flex-v"
              flat
              width="277"
            >
              <v-img
                :src="cover(sermon, 1)"
                :lazy-src="imageNotAvailable"
                :aspect-ratio="4 / 3"
                class="grey lighten-2 flex-init"
                @error="imageLoadError"
              >
                <v-expand-transition>
                  <div
                    v-if="hover"
                    class="d-flex transition-fast-in-fast-out v-card--reveal"
                    style="height: 100%;"
                  >
                    <v-btn dark round flat @click="playSermon(sermon)">
                      <v-icon left>play_circle_outline</v-icon>Play
                    </v-btn>
                    <v-btn dark round flat @click="downloadSermon(sermon)">
                      <v-icon left>file_download</v-icon>Download
                    </v-btn>
                  </div>
                </v-expand-transition>
              </v-img>

              <v-card-title class="px-1">
                <div>
                  <h3 class="font-weight-regular mb-1" style="font-size: 18px;">
                    {{ sermon.title }}
                  </h3>
                  <div class="text--secondary">{{ sermon.scripture }}</div>
                </div>
              </v-card-title>

              <v-spacer></v-spacer>

              <v-card-actions class="px-1">
                <span class="caption">{{ date(sermon) }}</span>
              </v-card-actions>
            </v-card>
          </v-hover>
        </template>
      </horizontal-list>
    </v-container>

    <!-- Tabs -->
    <v-divider class="grey"></v-divider>
    <v-tabs fixed-tabs class="mb-5">
      <v-tab
        v-for="(tab, i) in tabs"
        :key="i"
        @click="$vuetify.goTo(`#${tab.name}`, options)"
      >
        <v-icon class="mr-2">{{ tab.icon }}</v-icon>
        {{ tab.label }}
      </v-tab>
    </v-tabs>

    <!-- Recent sermons -->
    <v-container grid-list-lg py-5 id="recent-sermons">
      <div class="text-xs-center my-4">
        <v-divider class="grey"></v-divider>
        <h3 class="tabs-title">Recent</h3>
      </div>
      <v-layout v-if="recent.sermons.length !== 0" row wrap>
        <v-flex
          v-for="(sermon, i) in recent.sermons"
          :key="i"
          d-flex
          xs12
          sm6
          md4
          lg3
          xl2
        >
          <v-card class="elevation-3 d-flex flex-v">
            <v-img
              :src="cover(sermon, 1)"
              :lazy-src="imageNotAvailable"
              :aspect-ratio="4 / 3"
              class="hidden-sm-and-down grey lighten-2 flex-init"
              @error="imageLoadError"
            ></v-img>

            <v-layout row justify-space-between align-center>
              <v-flex md12 py-0>
                <v-card-title>
                  <div>
                    <span class="caption">{{ date(sermon) }}</span>
                    <h3
                      class="font-weight-regular mb-1"
                      style="font-size: 18px;"
                    >
                      {{ sermon.title }}
                    </h3>
                    <div class="text--secondary">{{ sermon.scripture }}</div>
                  </div>
                </v-card-title>
              </v-flex>
              <v-flex class="hidden-md-and-up" shrink pr-4 pl-0>
                <v-img
                  :src="cover(sermon, 1)"
                  :lazy-src="imageNotAvailable"
                  :aspect-ratio="4 / 3"
                  class="grey lighten-2 flex-init"
                  @error="imageLoadError"
                  width="100px"
                  contain
                ></v-img>
              </v-flex>
            </v-layout>

            <v-spacer></v-spacer>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn small round flat @click="playSermon(sermon)">
                <v-icon small left>play_circle_outline</v-icon>Play
              </v-btn>
              <v-btn small round flat @click="downloadSermon(sermon)">
                <v-icon small left>file_download</v-icon>Download
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
        <v-flex xs12 class="text-xs-center mt-2">
          <v-btn
            :loading="recent.loading"
            large
            outline
            @click="getRecentSermons"
            >See More</v-btn
          >
        </v-flex>
      </v-layout>
      <v-card v-else height="300" flat>
        <v-layout fill-height align-center>
          <v-flex text-xs-center>
            <v-icon x-large>cloud_off</v-icon>
            <h4 class="display-1 my-2">Unable to load any sermons</h4>
            <p class="subheading">
              Something went wrong. Try reloading the page again.
            </p>
            <v-btn color="primary" @click="getRecentSermons">Try Again</v-btn>
          </v-flex>
        </v-layout>
      </v-card>
    </v-container>

    <!-- Sermon series -->
    <v-container grid-list-lg py-5 id="sermon-series">
      <div class="text-xs-center my-4">
        <v-divider class="grey"></v-divider>
        <h3 class="tabs-title">Series</h3>
      </div>
      <v-layout v-if="series.sermons.length !== 0" row wrap justify-center>
        <v-flex
          v-for="(content, j) in series.sermons"
          :key="j"
          d-flex
          xs12
          sm6
          lg4
          xl3
        >
          <v-card flat color class="d-flex flex-v align-xs-start">
            <v-card-title primary-title class="px-1">
              <div>
                <span class="body-2 text-uppercase">Series</span>
                <h3 class="title">{{ content._id }}</h3>
              </div>
            </v-card-title>
            <v-img
              :src="cover(content.sermons[content.sermons.length - 1], 1)"
              :lazy-src="imageNotAvailable"
              :aspect-ratio="4 / 3"
              class="grey lighten-2 f-grow-0"
              @click="showSermonsBySeries(content)"
            >
              <v-container fill-height fluid class="overlay"></v-container>
            </v-img>
          </v-card>
        </v-flex>
        <v-flex xs12 class="text-xs-center mt-2">
          <v-btn :loading="series.loading" large outline @click="getSeriesList">
            See More
          </v-btn>
        </v-flex>
      </v-layout>
      <v-card v-else height="300" flat>
        <v-layout fill-height align-center>
          <v-flex text-xs-center>
            <v-icon x-large>cloud_off</v-icon>
            <h4 class="display-1 my-2">Unable to load series list</h4>
            <p class="subheading">
              Something went wrong. Try reloading the page again.
            </p>
            <v-btn color="primary" @click="getSeriesList">Try Again</v-btn>
          </v-flex>
        </v-layout>
      </v-card>
    </v-container>

    <!-- Scripture -->
    <v-container grid-list-lg py-5 id="scripture">
      <div class="text-xs-center my-4">
        <v-divider class="grey"></v-divider>
        <h3 class="tabs-title">Scriptures</h3>
      </div>
      <v-layout v-if="otBooks.length !== 0" row wrap>
        <v-flex xs12> <h3 class="title my-4">Old Testament</h3></v-flex>
        <v-flex v-for="(book, i) in otBooks" :key="i" xs6 sm4 md3 lg2>
          <v-card flat @click="getSermonsByBook(book)">
            <v-card-title>
              <h4 class="subheading blue--text text--darken-3">{{ book }}</h4>
            </v-card-title>
          </v-card>
        </v-flex>
        <v-flex xs12><h3 class="title my-4">New Testament</h3></v-flex>
        <v-flex v-for="(book, i) in ntBooks" :key="i" xs6 sm4 md3 lg2>
          <v-card flat @click="getSermonsByBook(book)">
            <v-card-title>
              <h4 class="subheading blue--text text--darken-3">{{ book }}</h4>
            </v-card-title>
          </v-card>
        </v-flex>
      </v-layout>
      <v-card v-else height="300" flat>
        <v-layout fill-height align-center>
          <v-flex text-xs-center>
            <v-icon x-large>cloud_off</v-icon>
            <h4 class="display-1 my-2">Unable to load books of the bible</h4>
            <p class="subheading">
              Something went wrong. Try reloading the page again.
            </p>
            <v-btn color="primary" @click="getBookList">
              Try Again
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card>
    </v-container>

    <v-dialog
      v-model="dialog.show"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="840"
    >
      <v-card v-if="!dialog.loading" tile>
        <v-container v-if="dialog.body.length != 0" pa-1 grid-list-md>
          <v-layout row wrap justify-space-around fill-height>
            <v-flex xs12 md5 grey lighten-4 pa-5>
              <v-layout wrap justify-center>
                <v-flex xs12 sm6 md12>
                  <v-img
                    :src="cover(dialog.body[dialog.body.length - 1], 1)"
                    :aspect-ratio="4 / 3"
                    class="grey lighten-2"
                    @error="imageLoadError"
                  ></v-img>
                </v-flex>
                <v-flex xs12 sm6 md12 :class="padding">
                  <h4 class="headline">{{ dialog.header }}</h4>
                  <p class="text--secondary">
                    {{ dialog.body.length }} Sermons
                  </p>
                  <v-btn
                    round
                    color="grey darken-2"
                    dark
                    @click="dialog.show = false"
                  >
                    Close
                    <v-icon right dark>close</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs12 sm9 md7 pa-3 :class="overflowY">
              <v-list three-line>
                <template v-for="(sermon, i) in dialog.body">
                  <v-list-tile
                    :key="sermon.title"
                    avatar
                    @click="playSermon(sermon)"
                  >
                    <v-list-tile-avatar size="60" tile class="mt-1">
                      <v-img
                        :src="cover(sermon, 1)"
                        :lazy-src="imageNotAvailable"
                        class="grey lighten-2"
                        @error="imageLoadError()"
                        width="70"
                      ></v-img>
                    </v-list-tile-avatar>
                    <v-list-tile-content class="ml-3">
                      <v-list-tile-sub-title class="caption text--primary">
                        {{ date(sermon) }}
                      </v-list-tile-sub-title>
                      <v-list-tile-title>{{ sermon.title }}</v-list-tile-title>
                      <v-list-tile-sub-title>
                        {{ sermon.scripture }}
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                    <v-spacer></v-spacer>
                  </v-list-tile>
                  <v-divider
                    v-if="i + 1 < dialog.body.length"
                    :key="i"
                  ></v-divider>
                </template>
              </v-list>
            </v-flex>
          </v-layout>
        </v-container>
        <v-container v-else>
          <v-layout>
            <h3 class="headline">No content available.</h3>
          </v-layout>
        </v-container>
      </v-card>
      <v-card v-else class="d-flex align-center" height="400px">
        <v-card-text class="text-xs-center">
          <v-progress-circular
            indeterminate
            :size="70"
            :width="7"
            color="primary"
            class="mb-0"
          ></v-progress-circular>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { bus } from "../main";
import AuthenticationService from "@/services/AuthenticationService";
import HorizotnalList from "./HorizontalList";
import ImageMixin from "@/mixins/imageMixin";
import DownloadMixin from "@/mixins/downloadMixin";
export default {
  name: "Sermons",
  components: {
    "horizontal-list": HorizotnalList
  },
  data() {
    return {
      banner: require("../assets/banner/sermons-banner.jpg"),
      imageNotAvailable: require("@/assets/image-not-available-3.jpg"),
      oldTestament: [
        "Genesis",
        "Exodus",
        "Leviticus",
        "Numbers",
        "Deuteronomy",
        "Joshua",
        "Judges",
        "Ruth",
        "1 Samuel",
        "2 Samuel",
        "1 Kings",
        "2 Kings",
        "1 Chronicles",
        "2 Chronicles",
        "Ezra",
        "Nehemiah",
        "Esther",
        "Job",
        "Psalm",
        "Proverbs",
        "Ecclesiastes",
        "Song of Solomon",
        "Isaiah",
        "Jeremiah",
        "Lamentations",
        "Ezekiel",
        "Daniel",
        "Hosea",
        "Joel",
        "Amos",
        "Obadiah",
        "Jonah",
        "Micah",
        "Nahum",
        "Habakkuk",
        "Zephaniah",
        "Haggai",
        "Zechariah",
        "Malachi"
      ],
      newTestament: [
        "Matthew",
        "Mark",
        "Luke",
        "John",
        "Acts",
        "Romans",
        "1 Corinthians",
        "2 Corinthians",
        "Galatians",
        "Ephesians",
        "Philippians",
        "Colossians",
        "1 Thessalonians",
        "2 Thessalonians",
        "1 Timothy",
        "2 Timothy",
        "Titus",
        "Philemon",
        "Hebrews",
        "James",
        "1 Peter",
        "2 Peter",
        "1 John",
        "2 John",
        "3 John",
        "Jude",
        "Revelation"
      ],
      tabs: [
        {
          icon: "date_range",
          label: "Recent",
          name: "recent-sermons"
        },
        {
          icon: "view_list",
          label: "Series",
          name: "sermon-series"
        },
        {
          icon: "library_books",
          label: "Scripture",
          name: "scripture"
        }
      ],
      featuredSermons: [],
      otBooks: [],
      ntBooks: [],
      recent: {
        sermons: [],
        loading: false,
        page: 1
      },
      series: {
        sermons: [],
        loading: false,
        page: 1
      },
      dialog: {
        header: "",
        body: [],
        show: false,
        loading: true
      }
    };
  },
  mounted() {
    this.getFeaturedSermons();
    this.getRecentSermons();
    this.getSeriesList();
    this.getBookList();
  },
  methods: {
    async getFeaturedSermons() {
      const response = await AuthenticationService.getFeaturedSermons();
      this.featuredSermons = response.data;
    },
    async getRecentSermons() {
      this.recent.loading = true;
      let limit = 8;
      const response = await AuthenticationService.getRecentSermons(
        limit,
        this.recent.page++
      );
      this.recent.sermons = this.recent.sermons.concat(response.data);
      this.recent.loading = false;
    },
    async getSeriesList() {
      let limit = 6;
      const response = await AuthenticationService.getSeriesList(
        limit,
        this.series.page++
      );
      this.series.sermons = this.series.sermons.concat(response.data);
      this.series.loading = false;
    },
    async getSermonsBySeries(series) {
      const response = await AuthenticationService.getSermonsBySeries(series);
      this.series.sermons = response.data;
    },
    showSermonsBySeries(series) {
      this.dialog.loading = true;
      this.dialog.show = true;
      this.dialog.header = series.sermons[0].series;
      this.dialog.body = series.sermons;
      this.dialog.loading = false;
    },
    async getBookList() {
      const response = await AuthenticationService.getBookList();
      this.otBooks = this.oldTestament.filter(b => response.data.includes(b));
      this.ntBooks = this.newTestament.filter(b => response.data.includes(b));
    },
    async getSermonsByBook(book) {
      this.dialog.loading = true;
      this.dialog.show = true;
      const response = await AuthenticationService.getSermonsByBook(book);
      this.dialog.header = book;
      this.dialog.body = response.data;
      this.dialog.loading = false;
    },
    downloadSermon(sermon) {
      let url = `${process.env.SERVER_URI}/sermons/download/${sermon._id}`;
      this.download(url);
    },
    playSermon(sermon) {
      bus.$emit("playSermon", sermon);
    }
  },
  computed: {
    typography() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "py-3";
        case "sm":
          return "pa-4";
        case "md":
        case "lg":
        case "xl":
          return "py-4";
        default:
          return "";
      }
    },
    padding() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "py-3";
        case "sm":
          return "pa-4";
        case "md":
        case "lg":
        case "xl":
          return "py-4";
        default:
          return "";
      }
    },
    overflowY() {
      if (this.$vuetify.breakpoint.mdAndUp) {
        return "overflow-y-auto";
      } else {
        return "";
      }
    },
    options() {
      return {
        duration: 300,
        offset: -60,
        easing: "easeInOutCubic"
      };
    },
    player() {
      return this.$refs.plyr.player;
    }
  },
  mixins: [ImageMixin, DownloadMixin]
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.overflow-y-auto {
  max-height: 90vh;
  overflow-y: auto;
}
.flex-v {
  flex-direction: column;
}
.flex-init {
  flex: initial !important;
}
.tabs-title {
  display: inline-block;
  margin: 0 auto;
  position: relative;
  top: -18px;
  background-color: #fff;
  padding: 0 24px;
  font-size: 28px;
  font-weight: 400;
  line-height: 36px;
}
.overlay {
  transition: background 400ms ease;
}
.overlay:hover {
  background-color: rgba(0, 0, 0, 0.54);
}
.v-card--reveal {
  align-items: center;
  bottom: 0;
  justify-content: center;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.54);
  /* opacity: .5; */
  position: absolute;
  width: 100%;
}
</style>


// WEBPACK FOOTER //
// src/components/Sermons.vue